export type PhaseTaskCounts = {
	taskCount?: number | null;
	doneCount?: number | null;
	/** Of `taskCount`, the tasks that are client-owned (see `isClientOwnedTask`). */
	clientOwnedTaskCount?: number | null;
	/** Of `clientOwnedTaskCount`, the ones that are done. */
	clientOwnedDoneCount?: number | null;
};

export type TaskGroupCompletion = {
	doneCount: number;
	taskCount: number;
	/**
	 * Integer 0–100. Read off the counts, NOT the phase's manual progress: under manual
	 * progress a PM can type 100% while tasks remain open, and the sign-off modal must
	 * reflect what will actually be signed for, which is task completion.
	 */
	percent: number;
};

export type PhaseTaskBreakdown = {
	/** True when the phase has no tasks at all — the modal renders prose, never NaN%. */
	empty: boolean;
	/** Null when the phase has no firm-owned tasks, so no dead "0 of 0" group renders. */
	firmOwned: TaskGroupCompletion | null;
	/** Null when the phase has no client-owned tasks — most phases. */
	clientOwned: TaskGroupCompletion | null;
};

/** A count the table rolled up, made safe: a whole number in [0, max], 0 for anything unusable. */
const safeCount = (raw: number | null | undefined, max: number): number =>
	typeof raw === 'number' && Number.isFinite(raw) && raw > 0 ? Math.min(Math.floor(raw), max) : 0;

const group = (doneCount: number, taskCount: number): TaskGroupCompletion | null =>
	taskCount === 0 ? null : {doneCount, taskCount, percent: Math.round((doneCount / taskCount) * 100)};

/**
 * Split the phase's done/total totals, as the scoping table rolls them up, into a
 * firm-owned and a client-owned group. Firm-owned is the remainder: every task that is
 * not client-owned. A group with no tasks comes back null rather than as 0 / 0, so the
 * modal shows only the populations that exist. Inconsistent inputs are clamped so that
 * no group ever reads more done than it has, or more than 100%.
 */
export function derivePhaseTaskBreakdown(counts: PhaseTaskCounts | null | undefined): PhaseTaskBreakdown {
	const taskCount = safeCount(counts?.taskCount, Infinity);
	const doneCount = safeCount(counts?.doneCount, taskCount);
	const clientTaskCount = safeCount(counts?.clientOwnedTaskCount, taskCount);
	const clientDoneCount = safeCount(counts?.clientOwnedDoneCount, Math.min(clientTaskCount, doneCount));
	const firmTaskCount = taskCount - clientTaskCount;
	const firmDoneCount = Math.min(doneCount - clientDoneCount, firmTaskCount);
	return {
		empty: taskCount === 0,
		firmOwned: group(firmDoneCount, firmTaskCount),
		clientOwned: group(clientDoneCount, clientTaskCount),
	};
}
